let moment
import(/* webpackChunkName: "momentTimezone" */ "moment-timezone").then(module => moment = module.default)

export const groupBy = (array, property) => {
  if (typeof array === 'undefined') {
    return {}
  }

  if (!property) {
    throw 'No property specified to groupBy'
  }

  return array.reduce((acc, value) => {
    const group = acc[value[property]]

    group ? group.push(value) : acc[value[property]] = [value]
    return acc
  }, {})
}

export const mapBy = (array, property) => {
  return array.reduce((map, obj) => {
    map[obj[property]] = obj
    return map
  }, {})
}

export const groupByProperties = (array, properties) => {
  if (typeof array === 'undefined') {
    return {}
  }

  return array.reduce((acc, value) => {
    let key = ''
    properties.forEach((property) => {
      key = key + value[property]
    })
    const group = acc[key]

    group ? group.push(value) : acc[key] = [value]
    return acc
  }, {})
}

export const getXSRFToken = () => {
  return getCookie('XSRF-TOKEN')
}

export const getCookie = (name) => {
  if (!document.cookie) {
    return null
  }

  const xsrfCookies = document.cookie.split(';')
  .map(c => c.trim())
  .filter(c => c.startsWith(name + '='))

  if (xsrfCookies.length === 0) {
    return null
  }

  return decodeURIComponent(xsrfCookies[0].split('=')[1])
}

export const formatHours = (hours, longWords = false, showAgo = longWords) => {
  let h, d, result, dayWord, hourWord
  d = Math.trunc(hours / 24)
  h = Math.abs(hours) - Math.abs(d * 24)

  result = ""
  if (longWords) {
    d = Math.abs(d)
    dayWord = d === 1 ? " day " : " days "
    hourWord = " hours "
  } else {
    dayWord = "d "
    hourWord = "h "
  }

  if (d !== 0) {
    result += d + dayWord
    if (h !== 0) {
      result += h + hourWord

    }
  } else {
    if (hours >= 0) {
      result += h + hourWord
    } else {
      result += "-" + h + hourWord
    }
  }

  if (showAgo) {
    if (hours > 0) {
      result += "ago"
    } else {
      result += "from now"
    }
  }

  return result.trim()
}

export const iodtFormat = "YYYYMMDDHHmmss.SSS"
export const dateOnlyFormat = "MM/DD/YY"
export const timeOnlyFormat = "h:mm A z"
export const dateTimeFormat = "MM/DD/YY hh:mm A z"
export const dateTime24HrFormat = "MM/DD/YY HH:mm z"
export const dateTime24HrNoTzFormat = "MM/DD/YY HH:mm"
export const dateTimeNoTzFormat = "MM/DD/YY hh:mm A"

export const merge = (...obj) => {
  return Object.assign({}, ...obj)
}

export const iodtUtcToTimezoneDateOnly = (iodt, timezone, displayNA = true) => {
  return iodtUtcToTimezoneFormat(iodt, timezone, dateOnlyFormat, displayNA)
}

export const iodtUtcToTimezoneTimeOnly = (iodt, timezone, displayNA = true) => {
  return iodtUtcToTimezoneFormat(iodt, timezone, timeOnlyFormat, displayNA)
}

export const iodtUtcToTimezoneDateTime = (iodt, timezone, displayNA = true) => {
  return iodtUtcToTimezoneFormat(iodt, timezone, dateTimeFormat, displayNA)
}

export const iodtUtcToTimezoneDateTime24Hr = (iodt, timezone, displayNA = true) => {
  return iodtUtcToTimezoneFormat(iodt, timezone, dateTime24HrFormat, displayNA)
}

export const iodtUtcToTimezoneDateTimeHideTz = (iodt, timezone, displayNA = true) => {
  return iodtUtcToTimezoneFormat(iodt, timezone, dateTimeNoTzFormat, displayNA)
}

export const iodtUtcToBrowserDateTime = (iodt, displayNA = false) => {
  if (!moment) {
    return ''
  }
  return iodtUtcToTimezoneFormat(iodt, moment.tz.guess(), dateTimeFormat, displayNA)
}

export const iodtUtcToBrowserDateTimeHideTz = (iodt) => {
  if (!moment) {
    return ''
  }
  return iodt ? moment.utc(iodt, iodtFormat).tz(moment.tz.guess()).format(dateTimeNoTzFormat)
      : 'N/A'
}

export const iodtUtcToTimezoneFormat = (iodt, timezone, format, displayNA = true) => {
  if (!moment) {
    return ''
  }
  return (iodt && timezone) ? moment.utc(iodt, iodtFormat).tz(timezone).format(format)
      : displayNA ? 'Not Available' : ''
}

export const localFormattedToIodtUtc = (localFormatted, timezone) => {
  if (!moment) {
    return ''
  }
  return moment.tz(localFormatted, dateTime24HrNoTzFormat, timezone).tz('utc').format(iodtFormat)
}

export const iodtToDateOnly = (iodt) => {
  return iodtWithFormat(iodt, dateOnlyFormat)
}

export const dateTimeOnlyToIodt = (date) => {
  if (!moment) {
    return ''
  }
  return date ? moment(date, dateTime24HrNoTzFormat).format(iodtFormat) : ''
}

export const dateTimeOnlyToYYYYMMDD = (dateOnlyString) => {
  if (!moment) {
    return ''
  }
  return dateOnlyString ? moment(dateOnlyString, dateOnlyFormat).format('YYYYMMDD') : ''
}

export const iodtToDateTimeHideTz = (iodt) => {
  return iodtWithFormat(iodt, dateTimeNoTzFormat)
}

export const iodtToDateTimeHideTz24Hr = (iodt) => {
  return iodtWithFormat(iodt, dateTime24HrFormat)
}

export const localIodtToDateTimeWithTz24Hr = (iodt, timezone) => {
  if (!moment) {
    return ''
  }
  return iodt ? moment.tz(iodt, iodtFormat, timezone).format(dateTime24HrFormat) : ''
}

export const localIodtToUtc = (iodt, timezone) => {
  if (!moment) {
    return ''
  }
  return iodt ? moment.tz(iodt, iodtFormat, timezone).tz('utc').format(iodtFormat) : ''
}

export const iodtWithFormat = (iodt, format) => {
  if (!moment) {
    return ''
  }
  return iodt ? moment(iodt, iodtFormat).format(format) : ''
}

export const iodtUtcToTimestamp = (iodt, timezone) => {
  if (!moment) {
    return null
  }
  return iodt ? moment.utc(iodt, iodtFormat).tz(timezone) : null
}

export const iodtUtcToDateOnlyForLfd = (iodt, timezone, backDateAm, displayNA = true) => {
  // if backDateAm is true then lfd will be set to the prior day if time is before 6am
  if (!moment) {
    return ''
  }
  if (!iodt || !timezone) {
    return displayNA ? 'Not Available' : ''
  }
  let m = moment.utc(iodt, iodtFormat).tz(timezone)
  if (backDateAm && m.hours() < 6) {
    m = m.subtract(1, 'days')
  }
  return m.format(dateOnlyFormat)
}

export const iodtUtcToTzDateOnly = (iodt, timezone, displayNA = true) => {
  if (!moment) {
    return ''
  }
  if (!iodt || !timezone) {
    return displayNA ? 'Not Available' : ''
  }
  return moment.utc(iodt, iodtFormat).tz(timezone).format(dateOnlyFormat)
}

export const iodtLocalToDateOnlyForPickup = (iodt, timezone, backdateAm) => {
  // backDateAm is true only for LALB DEM (app currently only supports LALB QR/CED)
  if (!moment || !iodt || !timezone) {
    return ''
  }
  let m = moment.tz(iodt, iodtFormat, timezone)
  if (backdateAm && m.hours() < 12) {
    m = m.subtract(1, 'days')
  }
  return m.format(dateOnlyFormat)
}

export const calendarDaysUntil = (utcIodt1, timezone) => {
  if (!moment) {
    return 0
  }
  // Measure differences between now and future date by counting the difference of the calendar day number
  const thenLocal = moment.utc(utcIodt1, iodtFormat).tz(timezone)
  const nowLocal = moment.tz(timezone)
  return hoursBetween(thenLocal.startOf('day'), nowLocal.startOf('day')) / 24
}

export const hoursBetween = (date1, aDate2) => {
  if (!moment) {
    return 0
  }
  let date2 = aDate2
  if (typeof aDate2 === 'string') {
    date2 = moment.utc(date2, iodtFormat)
  }
  return Math.floor((moment.utc(date1, iodtFormat) - date2) / (60000 * 60))
}

export const hoursBetweenUtc = (inDate1, inDate2) => {
  if (!moment) {
    return 0
  }
  const date1 = (typeof inDate1 === 'string') ? moment.tz(inDate1, iodtFormat, 'UTC') : inDate1
  const date2 = (typeof inDate2 === 'string') ? moment.tz(inDate2, iodtFormat, 'UTC') : inDate2
  return Math.floor((date1 - date2) / (60000 * 60))
}

export const pluralize = (number, string) => {
  return number === 1 ? string : string + 's'
}

export const capitalizeFirstLetter = (string) => {
  if (!string) {
    return ''
  }
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const capitalizeFirstLetterOnly = (string) => {
  if (!string) {
    return ''
  }
  return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1)
}

export const unique = (arr) => {
  return arr.filter(function (elem, index, self) {
    return index === self.indexOf(elem)
  })
}

export const sanitizeDisplayString = (value) => {
  return value === undefined || value === null ? '' : value.toString()
}

export function getSingleOrDoubleClickHandler(onClick, onDblClick) {
  let timeoutID = null
  const delay = 500
  return function (event) {
    if (!timeoutID) {
      timeoutID = setTimeout(function () {
        onClick && onClick(event)
        timeoutID = null
      }, delay)
    } else {
      timeoutID = clearTimeout(timeoutID)
      onDblClick && onDblClick(event)
    }
  }
}

export function arraysEqual(a, b) {
  if (a instanceof Array && b instanceof Array) {
    if (a.length !== b.length) {
      return false
    }
    for (var i = 0; i < a.length; i++) {
      if (!arraysEqual(a[i], b[i])) {
        return false
      }
    }
    return true
  } else {
    return a === b
  }
}

export function formatContainerNumber(cntrNum) {
  let cntrNumUpperCase = cntrNum.toUpperCase().trim()
  if (cntrNumUpperCase.length > 10) {
    cntrNumUpperCase = cntrNumUpperCase.slice(0, 10)
  }
  return cntrNumUpperCase
}

export const keys = {
  tab: 9,
  enter: 13,
  esc: 27,
  space: 32,
  pageup: 33,
  pagedown: 34,
  end: 35,
  home: 36,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  asterisk: 106,
  shift: 16,
  ctrl: 17,
  backspace: 8
}

export const regexUrl = /^((?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i

export const validateUrl = (url, focused) => {
  if (url.length === 0 || focused) {
    return true
  }
  return regexUrl.test(url) && url.length <= 512
}

export const regexEmail = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const validateEmail = (email) => {
  return regexEmail.test(email)
}

export const validateEmailList = (string) => {
  const trimmed = string.trim()
  if (!trimmed) {
    return false
  }
  const emails = trimmed.split(/\s*[;,]\s*/)
  return emails.every(validateEmail)
}

export const sortArrayDefault = (array) => {
  return array.sort((a, b) => {
    const item1 = a.value.toUpperCase()
    const item2 = b.value.toUpperCase()
    if (item1 < item2) {
      return -1
    }
    if (item1 > item2) {
      return 1
    }
    return 0
  })
}

export const isString = (value) => {
  return typeof value === 'string' || value instanceof String
}

export const isObject = (value) => {
  return value && typeof value === 'object' && value.constructor === Object
}

export const objectKeys = (obj) => {
  return Object.keys(obj)
}

const formatter = new Intl.NumberFormat('en-US',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

export const formatNumber = (number) => {
  if (number === null || number === undefined) {
    return null
  }
  return formatter.format(number)
}

export const getSvvdCombinedWithName = (svvd, vesselName) => {
  const splitted = svvd.split(" ")
  const svv = splitted[0].split("-")
  const direction = splitted[1]
  return svv[0] + '-' + vesselName + '-' + svv[2] + ' ' + direction
}

export const displaySprintPayOption = () => {
  return false
}